import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import iosVideo from '../images/InstallationPromo-iOS.mp4';

// --- Styles ---
const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  padding: '20px'
};

const modalStyle = {
  backgroundColor: '#fff',
  width: '100%',
  maxWidth: '500px',
  maxHeight: '90vh',        // ← add this
  borderRadius: '12px',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
  overflow: 'hidden',
  overflowY: 'auto',        // ← add this
  textAlign: 'center',
};

const logInstallClick = async () => {
  try {
    const { db } = await import('../firebaseConfig');
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    const isDev = window.location.hostname === "localhost";
    await addDoc(collection(db, isDev ? "installClicksDev" : "installClicks"), {
      timestamp: serverTimestamp(),
      language: window.location.pathname.includes('/en/') ? 'en' : window.location.pathname.includes('/fr/') ? 'fr' : window.location.pathname.includes('/id/') ? 'id' : 'ar',
      platform: /android/i.test(navigator.userAgent) ? 'Android' :
        /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'iOS' : 'Other',
      userAgent: navigator.userAgent || '',
    });
  } catch (error) {
    console.error("Error logging install click:", error);
  }
};

// --- Component ---
const InstallAppBanner = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  const path = window.location.pathname;
  const isEnglish = path.includes('/en/');
  const isFrench = path.includes('/fr/');
  const isIndonesian = path.includes('/id/');
  const isRTL = !isEnglish && !isFrench && !isIndonesian;

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setIsIOS(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__deferredInstallPrompt = e; // ← add this
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleOpenModal = async () => {
    await logInstallClick();

    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else if (isIOS) {
      setOpen(true);
    }
  };

  if (!deferredPrompt && !isIOS) return null;

  const texts = {
    title: isEnglish ? 'Install the App' : isFrench ? 'Installer l\'application' : isIndonesian ? 'Pasang Aplikasi' : 'ثبت التطبيق على جوالك',
    button: isEnglish ? 'Install Now' : isFrench ? 'Installer maintenant' : isIndonesian ? 'Pasang Sekarang' : 'تثبيت الآن',
    modalTitle: isEnglish
      ? 'Do Calculate can be installed as an app'
      : isFrench
      ? 'Do Calculate peut être installé comme une application'
      : isIndonesian
      ? 'Do Calculate bisa dipasang sebagai aplikasi'
      : 'موقع احسب يمكن تحميله كتطبيق على جهازك',
    modalText: isEnglish
      ? 'Follow the instructions above to use the site as an app without opening it from the browser each time.'
      : isFrench
      ? 'Suivez les instructions ci-dessus pour utiliser le site comme une application sans l\'ouvrir depuis le navigateur à chaque fois.'
      : isIndonesian
      ? 'Ikuti petunjuk di atas untuk menggunakan situs ini sebagai aplikasi tanpa harus membukanya dari browser setiap saat.'
      : 'اتبع التعليمات الموضحة أعلاه حتى تستطيع استخدام الموقع كتطبيق دون الحاجة إلى فتحه من المتصفح في كل مرة.',
    ok: isEnglish ? 'OK' : isFrench ? 'OK' : isIndonesian ? 'OK' : 'موافق'
  };

  return (
    <div style={{
      display: 'flex',
      backgroundColor: '#f8f8f8',
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: 'Arial, sans-serif',
      width: '100%',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      alignItems: 'center'
    }}>
        <div style={{ flex: 1, padding: '2px 15px', textAlign: isRTL ? 'right' : 'left' }}>
        <p style={{ fontSize: 18, fontWeight: 'bold', margin: '5px' }}>{texts.title}</p>
      </div>
      <div style={{ padding: 2 }}>
        <button
          className="btn btn-success"
          onClick={handleOpenModal}
          data-install-banner-btn
          style={{ borderRadius: 10, fontSize: 18, padding: '10px 20px', border: 'none', cursor: 'pointer' }}
        >
          {texts.button}
        </button>
      </div>

      {open && createPortal(
        <div style={overlayStyle} onClick={() => setOpen(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={{ backgroundColor: '#198754', color: 'white', padding: '15px', borderRadius: '8px 8px 0 0', fontSize: '18px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{texts.modalTitle}</span>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <video
                playsInline autoPlay loop muted controls
                style={{ width: '100%', borderRadius: 8, marginBottom: 15 }}
              >
                <source src={iosVideo} type="video/mp4" />
              </video>
              <p style={{ fontSize: '16px', color: '#444', marginBottom: 20 }}>{texts.modalText}</p>
              <button
                className="bg-success"
                onClick={() => setOpen(false)}
                style={{ borderRadius: 10, fontSize: 18, padding: '12px', width: '100%', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                {texts.ok}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InstallAppBanner;